import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n/I18nProvider'
import type { MessageKey } from '../i18n/messages'
import { DateLisible } from '../components/Nombre'
import { Alert, Badge, Button, Card, DataTable, FormActions, PageHeader, SelectField, Stack, TextField, useToast } from '../components/ui'
import type { Column } from '../components/ui'
import { growthApi } from './growthApi'
import type { ClientRequest } from './growthApi'

/**
 * Les demandes des clients (migration 0081) : changer de formule, résilier, ajouter une option.
 *
 * CLORE UNE DEMANDE NE FAIT PAS LE GESTE. Le changement de formule, la résiliation, l'option se
 * font sur la fiche du client, avec leurs propres règles ; ici on dit au client que c'est fait,
 * ou pourquoi ce ne le sera pas — un refus exige une réponse.
 */
export function RequestsPage() {
  const { t, describeError } = useI18n()
  const { session } = useAuth()
  const { notify } = useToast()
  const token = session?.token ?? ''

  const [filtre, setFiltre] = useState('open')
  const [demandes, setDemandes] = useState<ClientRequest[]>([])
  const [cible, setCible] = useState<ClientRequest | null>(null)
  const [reponse, setReponse] = useState('')
  const [envoi, setEnvoi] = useState(false)

  const charger = useCallback(() => {
    if (token === '') return
    growthApi
      .listRequests(token, filtre === '' ? null : filtre)
      .then(setDemandes)
      .catch((cause: unknown) => notify(describeError(cause), 'erreur'))
  }, [token, filtre, notify, describeError])

  useEffect(charger, [charger])

  const clore = async (statut: 'done' | 'declined') => {
    if (cible === null) return
    setEnvoi(true)
    try {
      await growthApi.handleRequest(token, cible.id, statut, reponse.trim() === '' ? null : reponse.trim())
      notify(t('growth.requests.handled'), 'succes')
      setCible(null)
      setReponse('')
      charger()
    } catch (cause: unknown) {
      notify(describeError(cause), 'erreur')
    } finally {
      setEnvoi(false)
    }
  }

  const colonnes: Column<ClientRequest>[] = [
    { key: 'date', header: t('growth.requests.date'), render: (r) => <DateLisible valeur={r.createdAt.slice(0, 10)} /> },
    { key: 'client', header: t('billing.col.client'), render: (r) => <Link to={`/platform/clients/${r.organizationId}`}>{r.clientName}</Link> },
    {
      key: 'kind',
      header: t('growth.requests.kind'),
      render: (r) => (
        <>
          {t(`growth.requestKind.${r.kind}` as MessageKey)}
          {r.requestedPlanCode !== null && <> → <strong>{r.requestedPlanCode}</strong></>}
        </>
      ),
    },
    { key: 'message', header: t('growth.requests.message'), render: (r) => r.message ?? '—' },
    {
      key: 'status',
      header: t('billing.col.status'),
      render: (r) =>
        r.status === 'open' ? (
          <Button variant="secondaire" onClick={() => setCible(r)}>{t('growth.requests.handle')}</Button>
        ) : (
          <span title={r.response ?? ''}>
            <Badge tone={r.status === 'done' ? 'succes' : 'info'}>{t(`growth.requestStatus.${r.status}` as MessageKey)}</Badge>
          </span>
        ),
    },
  ]

  return (
    <Stack>
      <PageHeader title={t('growth.requests.title')} description={t('growth.requests.description')} />
      <Card title={t('growth.requests.list')}>
        <Stack gap={3}>
          <div style={{ maxWidth: '16rem' }}>
            <SelectField
              label={t('billing.col.status')}
              value={filtre}
              onChange={setFiltre}
              options={[
                { value: 'open', label: t('growth.requestStatus.open') },
                { value: 'done', label: t('growth.requestStatus.done') },
                { value: 'declined', label: t('growth.requestStatus.declined') },
                { value: '', label: t('billing.status.all') },
              ]}
            />
          </div>
          <DataTable columns={colonnes} rows={demandes} rowKey={(r) => r.id} emptyLabel={t('growth.requests.empty')} />
          {cible !== null && (
            <Alert tone="info" title={`${cible.clientName ?? ''} — ${t(`growth.requestKind.${cible.kind}` as MessageKey)}`}>
              <Stack gap={2}>
                <p className="text-muted">{t('growth.requests.handleHint')}</p>
                <TextField label={t('growth.requests.response')} value={reponse} onChange={setReponse} />
                <FormActions>
                  <Button variant="secondaire" onClick={() => setCible(null)}>{t('common.cancel')}</Button>
                  <Button variant="danger" onClick={() => clore('declined')} disabled={reponse.trim() === ''} pending={envoi}>
                    {t('growth.requests.decline')}
                  </Button>
                  <Button onClick={() => clore('done')} pending={envoi}>{t('growth.requests.done')}</Button>
                </FormActions>
              </Stack>
            </Alert>
          )}
        </Stack>
      </Card>
    </Stack>
  )
}
